import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { OccupancyType } from 'src/app/shared/models/occupancy';
import { StringHelper } from 'src/app/shared/utils/string-helper';
import { ColorHelper } from 'src/app/shared/utils/color-helper';
import { Observable } from 'rxjs';
import { DateHelper } from 'src/app/shared/utils/date-helper';
import { ICreateReservationParams } from '../create-reservation-dialog/create-reservation-dialog-base.component';

@Component({
  selector: 'app-room-schedule-item',
  templateUrl: './room-schedule-item.component.html',
  styleUrls: ['./room-schedule-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoomScheduleItemComponent implements OnInit {

  @Input() scheduleItem: ScheduleItem;

  /**
   * Start hour of the table
   *
   * @memberof RoomScheduleComponent
   */
  @Input() fromHour: number;

  /**
   * End hour of the table
   *
   * @memberof RoomScheduleComponent
   */
  @Input() toHour: number;

  /**
   * Event raised on click on "create new"
   *
   * @memberof RoomScheduleItemComponent
   */
  @Output() createReservation = new EventEmitter<ICreateReservationParams>();

  constructor() { }

  ngOnInit() {
    if (this.fromHour === undefined) {
      throw new Error('Please provide property ' + nameof<RoomScheduleItemComponent>('fromHour'));
    }
    if (this.toHour === undefined) {
      throw new Error('Please provide property ' + nameof<RoomScheduleItemComponent>('toHour'));
    }
  }

  /**
   * Calculates the space relative to the top of the container
   *
   * @param {ScheduleItem} item
   * @returns {string}
   * @memberof RoomScheduleComponent
   */
  getPanelTop(): string {
    const span = this.scheduleItem.from.getMinutes();
    return String(span / 60 * 100) + '%';
  }

  /**
   * Calculates the relative height of the panel
   *
   * @param {ScheduleItem} item
   * @returns {string}
   * @memberof RoomScheduleComponent
   */
  getPanelHeight(): string {
    // adjust start time
    const start = new Date(this.scheduleItem.from);
    if (start.getHours() < this.fromHour) {
      start.setHours(this.fromHour);
      start.setMinutes(0);
    }

    // adjust end time
    const end = new Date(this.scheduleItem.to);
    if (end.getHours() >= this.toHour + 1) {
      end.setHours(this.toHour + 1);
      end.setMinutes(0);
    }

    if ((end.getTime() - start.getTime()) < 5 * 60 * 1000) { // 5 min margin
      return '0';
    }

    // calculate height
    const span = Math.floor((end.getTime() - start.getTime()) / 1000);
    return String(span / 3600 * 2) + 'rem';
  }

  /**
   * Helper to format time-span of the occupancy
   *
   * @param {Occupancy} occ
   * @returns {string}
   * @memberof RoomScheduleComponent
   */
  formatTimeSpan(occ: ScheduleItem): string {
    if (occ === undefined) {
      throw new Error('occ undefined');
    }

    return StringHelper.formatTimeSpan(
      occ.from.getHours(),
      occ.from.getMinutes(),
      occ.to.getHours(),
      occ.to.getMinutes()
    );
  }

  /**
   * raise createReservation-event
   *
   * @param {ScheduleItem} scheduleItem
   * @memberof RoomScheduleItemComponent
   */
  onClickCreateReservation(scheduleItem: ScheduleItem): void {
    this.createReservation.emit({
      idReservation: null,
      idRessource: scheduleItem.idRessource,
      from: new Date(scheduleItem.from),
      to: new Date(scheduleItem.to),
    });
  }

}

/**
 * Panels displayed in table-cells (day wise)
 * Note: one Occupany might be many ScheduleItem
 *
 * @class ScheduleItem
 */
export class ScheduleItem {

  idRessource: number;

  from: Date;

  to: Date;

  designation: string;

  description: string;

  color: string;

  textColor: string;

  type: OccupancyType;

  isBookable: boolean;

  constructor(idRessource: number, from: Date, to: Date, text: string,
    description: string, defaultText: Observable<any>, color: string, occupanyType: OccupancyType) {
    this.idRessource = idRessource;
    this.description = description;
    this.from = from;
    this.to = to;
    // replace color white with defined darker gray color
    this.color = !StringHelper.hasAny(color) ||
        ['white', '#ffffff'].includes(color) ||
        `${color}`.replace(/\s+/g, '').includes('(255,255,255)')
      ? '#dbdbdb'
      : color;

    this.type = occupanyType;
    this.isBookable = occupanyType === OccupancyType.Reservable;

    if (StringHelper.hasAny(text)) {
      this.designation = text;
    } else {
      defaultText.subscribe(x => this.designation = x);
    }

    this.setTextColor();
  }

  /**
   * Create empty elements to click to reserve
   *
   * @static
   * @param {Array<ScheduleItem>} items
   * @returns {Array<ScheduleItem>}
   * @memberof ScheduleItem
   */
  public static createEmptyScheduleItems(
    idRessource: number,
    items: Array<ScheduleItem>,
    startDate: Date,
    endDate: Date,
    text: Observable<any>,
    isBookable: boolean
  ): Array<ScheduleItem> {
    // 20 minutes
    const margin = 20 * 60 * 1000;

    // merge overlapping elements into one
    const workingElements = new Array<ScheduleItem>();
    items.sortByProp(x => x.from).forEach(element => {
      const i = workingElements.findIndex((si) => si.IntersectsExclude(element));
      if (i !== -1) {
        // expand the found element to the combined range
        workingElements[i].from = DateHelper.Min(workingElements[i].from, element.from);
        workingElements[i].to = DateHelper.Max(workingElements[i].to, element.to);
      } else {
        workingElements.push(element);
      }
    });

    const gaps = new Array<ScheduleItem>();
    let lastEnd = null;

    // loop to find gaps
    workingElements.sortByProp(x => x.from).forEach(element => {
      if (lastEnd === null) {
        lastEnd = startDate;
      }

      const dateDiff = element.from.getTime() - lastEnd.getTime();
      if (dateDiff > margin) {
        gaps.push(ScheduleItem.createClickable(idRessource, lastEnd, element.from, null, null, text, isBookable));
      }

      lastEnd = new Date(element.to.getTime() + 1); // eg. adjust 5:59:59.999 to 6:00:00.000
    });

    if (lastEnd === null) {
      // no occupancies
      gaps.push(ScheduleItem.createClickable(idRessource, startDate, endDate, null, null, text, isBookable));
    } else if ((endDate.getTime() - margin) > lastEnd.getTime()) {
      // add last item
      gaps.push(ScheduleItem.createClickable(idRessource, lastEnd, endDate, null, null, text, isBookable));
    }

    return gaps;
  }

  private static createClickable(idRessource: number, start: Date, end: Date,
    text: string, description: string, defaultText: Observable<any>, isBookable: boolean
  ): ScheduleItem {
    return new ScheduleItem(idRessource, start, end, text, description, defaultText, '#00FF00', isBookable
      ? OccupancyType.Reservable
      : OccupancyType.Unavailable); // todo tosc
  }

  private setTextColor() {
    const lightText = '#f8f9fa'; // todo tosc
    const darkText = '#343a40'; // todo tosc
    this.textColor = ColorHelper.colorDifference(this.color, lightText) > ColorHelper.colorDifference(this.color, darkText)
      ? lightText : darkText;
  }

  /**
   * returns true if range @param {other} intersects this range, <b>excluding</b> the exact timestamp (false if the ranges touch)
   *
   * @param {ScheduleItem} other
   * @returns {boolean}
   * @memberof ScheduleItem
   */
  public IntersectsExclude(other: ScheduleItem): boolean {
    if (other.to <= this.from || other.from >= this.to || this.to <= other.from || this.from >= other.to) {
      return false;
    }
    return true;
  }

}
